import StarIcon from '@mui/icons-material/Star';
import EditIcon from '@mui/icons-material/Edit';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, IconButton, Link } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ProgressBar } from './components/ProgressBar';
import { Favorite, ForecastYears, StockRow } from './types';
import { getClassColor } from './utils/getClassColor';
import { getGoalValue } from './utils/getGoalValue';
import { percentageComparator } from './utils/stockRows';

/** Campos que aparecem no celular na aba Empresas. */
const MOBILE_COMPANY_FIELDS = ['favorite', 'ticker', 'price', 'score'];

export function formatPrice(row: Pick<StockRow, 'currency' | 'price'>) {
  return `${row.currency === 'BRL' ? 'R$' : '$'}${row.price}`;
}

export function formatGoalLabel(row: Pick<StockRow, 'quantity' | 'goal'>) {
  return `${row.quantity} de ${row.goal ? Math.round(Number(row.goal) ?? 0) : ''}`;
}

/** Dados da linha para abrir o modal de edição. */
export function toEditingFavorite(row: StockRow): Favorite {
  return { ticker: row.ticker, quantity: Number(row.quantity), goal: Number(row.goal ?? 0) };
}

type ColumnOptions = {
  /** true na aba Carteira, false na aba Empresas. */
  isWallet: boolean;
  isMobile: boolean;
  years: ForecastYears;
  isFavorite: (ticker: string) => boolean;
  onToggleFavorite: (ticker: string) => void;
  onEdit: (favorite: Favorite) => void;
};

export function getStockColumns({
  isWallet,
  isMobile,
  years,
  isFavorite,
  onToggleFavorite,
  onEdit,
}: ColumnOptions): GridColDef<StockRow>[] {
  const columns: GridColDef<StockRow>[] = [
    {
      field: 'favorite',
      headerName: '',
      headerAlign: 'center',
      align: 'center',
      width: 30,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      resizable: false,
      renderCell: ({ row }) => {
        const color = isFavorite(row.ticker) ? 'primary' : 'default';

        if (isWallet) {
          return (
            <IconButton onClick={() => onEdit(toEditingFavorite(row))} color={color}>
              <EditIcon />
            </IconButton>
          );
        }

        return (
          <IconButton onClick={() => onToggleFavorite(row.ticker)} color={color}>
            {isFavorite(row.ticker) ? <StarIcon /> : <StarBorderIcon />}
          </IconButton>
        );
      },
    },
    {
      field: 'ticker',
      headerName: 'Ticker',
      headerAlign: 'center',
      align: 'center',
      editable: false,
      resizable: false,
      renderCell: ({ value }) => (
        <Link
          href={`https://finance.yahoo.com/quote/${value}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ fontWeight: 'medium' }}
        >
          {value}
        </Link>
      ),
    },
    {
      field: 'price',
      headerName: 'Preço',
      headerAlign: 'center',
      align: 'center',
      editable: false,
      resizable: false,
      renderCell: ({ row }) => formatPrice(row),
    },
    {
      field: 'changePct',
      headerName: 'Variação',
      headerAlign: 'center',
      align: 'center',
      editable: false,
      resizable: false,
      cellClassName: ({ value }) => getClassColor(value as string),
    },
    {
      field: 'score',
      headerName: 'Score',
      headerAlign: 'center',
      align: 'center',
      editable: false,
      resizable: false,
      renderCell: ({ value }) => (
        <Box sx={{ bgcolor: 'white', width: '100%' }}>
          <ProgressBar value={Number(value ?? 0)} label={`${Math.round(value ?? 0)}%`} />
        </Box>
      ),
    },
    {
      field: 'goal',
      headerName: 'Quantidade',
      headerAlign: 'center',
      align: 'center',
      editable: false,
      width: 210,
      renderCell: ({ row }) => (
        <Box sx={{ bgcolor: 'white', width: '100%' }}>
          <ProgressBar value={getGoalValue(row.quantity, row.goal)} label={formatGoalLabel(row)} />
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Tendência',
      headerAlign: 'center',
      align: 'center',
      editable: false,
      minWidth: 220,
      flex: 1,
    },
    {
      field: 'lastDY',
      headerName: `DY ${Number(years.start) - 1}`,
      headerAlign: 'center',
      align: 'center',
      editable: false,
      sortComparator: percentageComparator,
    },
    {
      field: 'startDY',
      headerName: `DY ${years.start}`,
      headerAlign: 'center',
      align: 'center',
      editable: false,
      sortComparator: percentageComparator,
    },
    {
      field: 'endDY',
      headerName: `DY ${years.end}`,
      headerAlign: 'center',
      align: 'center',
      editable: false,
      sortComparator: percentageComparator,
    },
  ];

  if (isWallet) return columns;

  return columns.filter(
    (c) => c.field !== 'goal' && (!isMobile || MOBILE_COMPANY_FIELDS.includes(c.field))
  );
}
